// 시퀀스 상태, 두 열과 두 뷰가 함께 읽는 한 자리
//
// attack 뷰의 좌열 폼과 우열 목록, 그리고 roster 뷰가 **같은 시퀀스 한 벌**을 봐야
// 한다. 각자 읽으면 confirm 직후의 한 순간에 좌열은 새 줄을 알고 우열은 모르는 상태가
// 생기고, 그 어긋남은 화면에서 「적재가 안 됐다」로 보인다.
//
// ── 이 화면은 시계를 읽지 않는다 ────────────────────────────────────────────
//
// **구성 화면이 그리는 값에 절대 시각이 들어갈 자리가 없다.** 개시 Δ 는 앵커 상수
// `r_i = offset_i − rallying_i − marching_i` 의 차이로만 정해지고 착탄 Δ 는 `offset_i`
// 그 자체다. 둘 다 시각과 무관하다.
//
// 그래서 delta 전용 계약이 「조심해서 안 그리는 것」이 아니라 **그릴 값 자체가 없는 것**이
// 된다. 서버가 `impactMainAt` 을 보내 주지만 이 층은 그것을 읽지 않는다 — 읽는 곳은
// 열람 화면이다. 명부가 게임 ID 를 응답에 안 실어 목록에서 샐 수 없게 만든 것과 같은
// 문법이고, 규율보다 구조가 오래간다.
//
// ── 소켓을 스스로 열지 않는다 ──────────────────────────────────────────────
//
// 탭 슬롯의 `render` 가 인자를 받지 않아 셸의 접속 상태가 여기까지 props 로 닿지 않는다.
// 그 타입은 세 탭이 함께 쓰는 것이라 한 스텝이 혼자 고칠 자리가 아니다. 그래서 이 층이
// 처음에는 **자기 소켓을 열었는데 그것이 결함이었다** — 서버의 연결 표는 세션 토큰 하나당
// 소켓 하나만 들고 새 소켓이 붙으면 앞 소켓을 끊는다(승계 처리). 셸의 구독과 이 구독이
// 서로를 쫓아내며 2초마다 깜빡였고, 화면에는 「가끔 반영이 2초 늦는다」로만 보였다.
//
// 지금은 `subscribeServerEvents` 로 **소켓 하나를 함께 쓴다.** 구독은 여럿, 소켓은 하나다.
// (2026-08-13 반영 지연 측정의 이상치가 이것을 드러냈다.)
//
// ── 명부가 바뀌면 시퀀스가 다시 온다 ────────────────────────────────────────
//
// 서버의 시퀀스 층이 명부 변경 이벤트를 구독해 **구성 중 시퀀스만** 다시 방송한다.
// 내보낸 시퀀스는 방송하지 않고, 방송되더라도 값이 안 움직인다 — 읽는 쪽이 상태를 보고
// 명부의 지금 값과 얼어붙은 값 중 하나를 고르기 때문이다. 이쪽이 할 일은 그 방송을 받아
// 다시 그리는 것뿐이다.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { Rally, Roster, Sequence } from '../../../../contract/schema.ts'
import { duration, instant, toMillis } from '../../../../core/time/brand.ts'
import { anchorConstant, type RallyDraft, type RallyKind } from '../../../../core/timing/attack.ts'
import {
  LEAD_FLOOR,
  previewRallyingOptions,
  RALLYING_OPTIONS,
} from '../../../../core/timing/lower-bound.ts'
import { call, routes, subscribeServerEvents } from '../../../access/client.ts'
import { accept, issue, newReadOrder } from '../../../access/read-order.ts'

/** 계약의 rally 를 계산 층이 받는 모양으로 옮긴다. 값을 바꾸지 않는다. */
function toDraft(line: Rally): RallyDraft {
  return {
    id: line.id,
    kind: line.type as RallyKind,
    rallying: duration(line.rallyingMs),
    marching: duration(line.marchingMs),
    offset: duration(line.offsetMs),
  }
}

/** 앵커 상수 `r_i`. **계산은 타이밍 코어가 하고 여기서는 부르기만 한다.** */
export function constantOf(line: Rally): number {
  return toMillis(anchorConstant(toDraft(line)))
}

export interface RallyRow {
  rally: Rally
  /** 가장 이른 개시 대비 Δ. **시각이 아니라 차이다.** */
  openDeltaMs: number
  /** main 착탄 대비 Δ. `offset` 그 자체다. */
  impactDeltaMs: number
  /** 개시부터 착탄까지 걸리는 시간 = 대기 + 행군. 기간이지 시각이 아니다. */
  openToImpactMs: number
}

/**
 * 시퀀스를 표시용 줄로 옮긴다. **여기에 `now` 도 `impactMainAt` 도 들어오지 않는다.**
 *
 * 정렬은 부르는 쪽이 고른다 — 개시순과 착탄순 둘 다 이 값들로 정해진다.
 */
export function toRows(rallies: readonly Rally[]): RallyRow[] {
  if (rallies.length === 0) return []
  const constants = rallies.map(constantOf)
  const earliest = Math.min(...constants)
  return rallies.map((rally, index) => ({
    rally,
    openDeltaMs: (constants[index] ?? 0) - earliest,
    impactDeltaMs: rally.offsetMs,
    openToImpactMs: rally.rallyingMs + rally.marchingMs,
  }))
}

export interface RallyingOption {
  rallyingMs: number
  minutes: number
  /** 이 옵션으로 붙였을 때 그 줄의 개시 Δ. 0 이면 그 줄이 가장 먼저 연다. */
  openDeltaMs: number
  /** 이 옵션이 기존 줄들을 뒤로 미는 양. 자동 평행이동이 눈에 보이는 자리다. */
  shiftMs: number
  /** 개시부터 착탄까지. **조립 문맥에서 네 옵션을 실제로 가르는 값이 이것이다.** */
  openToImpactMs: number
  feasible: boolean
  /**
   * 개시까지 남는 여유. **진행 중 시퀀스에서만 값이 있다.**
   *
   * 조립 문맥에서 `null` 인 것은 빠뜨린 것이 아니라 그 값이 없기 때문이다 — 손잡이가
   * 가장 이른 개시를 붙들고 있어 네 옵션의 여유가 전부 손잡이 값과 같아진다. 있지도 않은
   * 구분을 숫자로 그리면 화면이 네 값을 다르게 말하는 척하게 된다.
   */
  leadMs: number | null
  /**
   * 이 옵션으로 하한을 정확히 만족시키는 최소 착탄 간격. **불가일 때 답이 되는 숫자다.**
   *
   * 없으면 화면은 「불가」만 말하고 CT 는 간격을 손으로 더듬게 되는데, 그 더듬는 일이
   * 이 앱이 없애려는 수기 작업 그 자체다.
   */
  minimumOffsetMs: number | null
}

/**
 * 진행 중 시퀀스의 문맥. **착탄 원점이 절대 시각으로 얼어붙었고 지금이 흐른다.**
 *
 * 조립 문맥에는 이것이 없다 — 거기서는 시각이 아예 없어서 하한을 판정할 근거 자체가 없다.
 */
export interface LiveContext {
  impactMainAt: number
  nowMs: number
}

/** 화면이 쓰는 시간 칸. 열람 화면의 `live-clock.ts` 가 같은 값을 같은 이유로 쓴다. */
const DISPLAY_STEP_MS = 100

/**
 * 흐르는 시각에서 온 값을 화면의 칸에 맞춘다. **남은 시간을 과장하지 않는 쪽으로 자른다.**
 *
 * 타이밍 코어의 형식기는 0.1초의 배수가 아닌 값을 거절한다. 그 방어는 옳고, 무르게 하는
 * 대신 화면이 자기 칸에 맞춰 자른다 — 열람 화면이 2026-08-14 에 같은 자리를 먼저 만났고
 * 거기서 정한 규칙이 버림이다. 올림은 4.91초 남았을 때 `0:05.0` 이라 적어 **있는 것보다
 * 많은 시간을 약속**하고, 그 약속을 믿은 사람은 늦게 누른다.
 */
export function toDisplayStep(millis: number): number {
  return Math.floor(millis / DISPLAY_STEP_MS) * DISPLAY_STEP_MS
}

/**
 * 최소 착탄 간격은 **반대로 올려 자른다.**
 *
 * 같은 원칙의 다른 얼굴이다. 최소값을 내려 자르면 화면이 「이만큼이면 됩니다」라고 적은
 * 값을 그대로 넣었을 때 서버가 거절한다 — 화면이 자기가 시킨 일을 스스로 막는 모양이 되고,
 * 그때 CT 는 무엇이 틀렸는지 알 길이 없다.
 */
function toDisplayStepUp(millis: number): number {
  return Math.ceil(millis / DISPLAY_STEP_MS) * DISPLAY_STEP_MS
}

/**
 * 네 옵션의 결과를 **동시에** 낸다.
 *
 * ── 왜 「개시 여유」가 아니라 이 셋인가 ─────────────────────────────────────
 *
 * 손잡이의 정의가 `가장 이른 개시 = 지금 + 손잡이 값` 이라, 편집 문맥에서는 어떤 줄을
 * 붙여도 가장 이른 개시가 제자리에 남는다. 그래서 새 줄이 가장 먼저 여는 경우 네 옵션의
 * 「개시 여유」가 **전부 손잡이 값과 같아진다** — 네 값이 같아지는 것이 계산이 틀려서가
 * 아니라 구조가 그렇다.
 *
 * 실제로 옵션마다 다른 것은 셋이다. 그 줄의 개시 Δ, 기존 줄이 밀리는 양, 그리고 개시부터
 * 착탄까지의 길이. 앞의 둘은 어느 한쪽만 움직이지만(그 줄이 가장 먼저 열면 개시 Δ 가
 * 0 에 붙고 대신 밀림이 커진다) **마지막 하나는 언제나 대기 시간만큼 다르다.** 그래서 네
 * 옵션이 항상 서로 다르게 보인다.
 *
 * ── 「불가」는 지금 구조에서 발화하지 않는다 ────────────────────────────────
 *
 * 하한 조건 `open ≥ 지금 + 6초` 를 편집 문맥에서 풀면 `손잡이 + (r_new − min) ≥ 6초` 인데,
 * 새 줄을 포함한 최솟값을 쓰므로 괄호 안은 0 이상이고 손잡이는 이미 6초 이상이다. 즉
 * **편집 문맥에서는 하한을 어기는 조합을 만들 수 없다.** 어길 수 있는 것은 `impact_main`
 * 이 절대 시각으로 얼어붙은 **내보낸 시퀀스**뿐이고, 거기에 줄을 더하는 문은 재구성
 * 스텝이 연다.
 *
 * 그래도 판정을 여기 적어 두는 이유는, 이 식이 조립 화면의 것이고 재구성 스텝이 쓸 때
 * 두 벌이 되면 안 되기 때문이다.
 *
 * ── 그 갈래가 2026-08-15 에 발화하기 시작했다 ──────────────────────────────
 *
 * 재구성 스텝이 진행 중 시퀀스에 줄을 붙이는 문을 열었다. 그쪽은 `impact_main` 이 절대
 * 시각으로 얼어붙어 있어 **새 줄이 앞 줄을 밀지 못한다** — 밀 수 있다면 그것은 이미 나간
 * 사람들의 착탄을 옮기는 일이고, counter rally 의 계약이 정확히 그것을 금지한다. 그래서
 * 진행 중 문맥에서는 옵션마다 갈리는 것이 「개시 Δ」가 아니라 **개시 여유**이고, 하한을
 * 어기는 조합이 실제로 만들어진다.
 *
 * **계산을 여기서 다시 짜지 않는다.** 타이밍 코어의 `previewRallyingOptions` 가 그 문맥의
 * 식을 이미 갖고 있고, 이 함수는 문맥을 골라 그것을 부르기만 한다.
 */
export function previewOptions(input: {
  existing: readonly Rally[]
  offsetMs: number
  marchingMs: number
  handleMs: number | null
  /** 진행 중 시퀀스면 온다. 조립·편집 문맥에서는 없다. */
  live?: LiveContext | null
}): RallyingOption[] {
  const floor = toMillis(LEAD_FLOOR)
  const live = input.live ?? null

  if (live !== null) {
    return previewRallyingOptions({
      impactMain: instant(live.impactMainAt),
      offset: duration(input.offsetMs),
      marching: duration(input.marchingMs),
      now: instant(live.nowMs),
    }).map((preview) => {
      const rallyingMs = toMillis(preview.rallying)
      // **자른 값으로 판정한다.** 원값으로 판정하고 자른 값을 그리면 화면이 「+0:06.0 인데
      // 불가」라고 말하는 순간이 생긴다 — 화면이 자기 상태를 잘못 말하는 그 실패다. 버림은
      // 언제나 원값 이하라 화면이 서버보다 관대해지는 방향으로는 틀리지 않는다.
      const leadMs = toDisplayStep(toMillis(preview.lead))
      return {
        rallyingMs,
        minutes: Math.round(rallyingMs / 60_000),
        // 진행 중 시퀀스에서는 원점이 얼어붙어 앞 줄이 밀리지 않는다. 둘 다 구조적으로 0 이고,
        // 그 0 이 곧 「main 착탄은 움직이지 않는다」가 화면에서 보이는 모양이다.
        openDeltaMs: 0,
        shiftMs: 0,
        openToImpactMs: rallyingMs + input.marchingMs,
        feasible: leadMs >= floor,
        leadMs,
        minimumOffsetMs: toDisplayStepUp(toMillis(preview.minimumOffset)),
      }
    })
  }

  const constants = input.existing.map(constantOf)

  return RALLYING_OPTIONS.map((option) => {
    const rallyingMs = toMillis(option)
    const candidate = input.offsetMs - rallyingMs - input.marchingMs
    const earliestBefore = constants.length === 0 ? candidate : Math.min(...constants)
    const earliestAfter = Math.min(earliestBefore, candidate)
    const openDeltaMs = candidate - earliestAfter
    return {
      rallyingMs,
      minutes: Math.round(rallyingMs / 60_000),
      openDeltaMs,
      shiftMs: earliestBefore - earliestAfter,
      openToImpactMs: rallyingMs + input.marchingMs,
      // 손잡이가 없는 조립 문맥에는 시각이 없어 하한을 판정할 근거 자체가 없다.
      feasible: input.handleMs === null || input.handleMs + openDeltaMs >= floor,
      leadMs: null,
      minimumOffsetMs: null,
    }
  })
}

export interface RallyInput {
  type: RallyKind
  rosterId: string
  rallyingMs: number
  offsetMs: number
}

/**
 * 조작의 결과. **실패 문구와 그 조작이 만든 시퀀스를 함께 준다.**
 *
 * 둘을 함께 주는 이유가 있다. 부르는 쪽이 「무엇이 새로 생겼는가」를 알려면 조작 전후를
 * 견주어야 하는데, 조작 직후에 훅의 상태를 읽으면 React 의 갱신이 아직 반영되지 않아 옛
 * 값이 잡힌다. 그러면 「방금 적재」 표시가 **한 박자 늦게 늘 앞 줄에** 붙는다. 화면이 틀린
 * 줄을 가리키는 것은 아무것도 안 가리키는 것보다 나쁘다.
 *
 * 목록의 전이를 지켜보는 방법도 써 봤지만 그쪽은 **남의 방송에도 반응한다** — 명부에서
 * 사람이 지워져 그 줄이 빠지거나, 다른 창의 조작으로 목록이 흔들리는 순간에도 배지가
 * 움직였다. 응답으로 정하면 그 일이 생길 수 없다.
 */
export interface ActionOutcome {
  /** 실패하면 사람이 읽을 한 문장, 성공하면 `null`. 예외를 던지지 않는다. */
  error: string | null
  /** 성공했을 때의 새 시퀀스. 실패하면 `null`. */
  next: Sequence | null
}

export type ActionResult = Promise<ActionOutcome>

/**
 * 내보낸 작전의 한 줄을 고칠 때 보내는 것.
 *
 * **행군도 타입도 없다.** 행군을 고치는 문은 명부 하나뿐이고, main 은 착탄축의 원점이라
 * 타입이 바뀌면 나머지 줄의 간격이 전부 무엇 대비인지 모르는 값이 된다. 계약이 그 둘을
 * 아예 받지 않으므로 화면도 그 칸을 그리지 않는다.
 */
export interface RallyEditInput {
  rallyId: string
  rosterId: string
  rallyingMs: number
  offsetMs: number
}

export interface SequenceStore {
  sequence: Sequence | null
  /** 아군만. rally 를 여는 것은 아군이고 적 roster 는 수비 갈래의 몫이다. */
  allies: Roster[]
  loaded: boolean
  addRally: (input: RallyInput) => ActionResult
  removeRally: (rallyId: string) => ActionResult
  confirmSequence: () => ActionResult
  setHandle: (handleMs: number) => ActionResult
  dispatch: () => ActionResult
  discard: () => ActionResult
  /**
   * 편성을 그대로 둔 채 편집 문맥으로 되돌린다. **접기와 정확히 반대쪽이다** — 접기는
   * 편성을 버리고 이쪽은 지킨다. 되돌린 뒤에는 줄을 빼고 더하고 타입을 바꿀 수 있다.
   */
  reopen: () => ActionResult
  /** 같은 편성으로 시간만 다시 잡는다. 30초 원클릭과 직접 설정이 이 문 하나를 쓴다. */
  retime: (handleMs: number) => ActionResult
  /** 내보낸 작전의 한 줄을 고친다. 위반은 서버가 막고 화면은 그 문장을 그대로 보인다. */
  editRally: (input: RallyEditInput) => ActionResult
}

export function useSequence(ctToken: string | null): SequenceStore {
  const [sequence, setSequence] = useState<Sequence | null>(null)
  const [allies, setAllies] = useState<Roster[]>([])
  const [loaded, setLoaded] = useState(false)

  // 소켓이 살아 있는 동안 최신 콜백을 보게 한다. 소켓을 매번 다시 열면 그때마다 서버가
  // 접속 명단을 다시 방송하고, 전투 중에 그 잡음이 CT 화면에서 깜빡임으로 보인다.
  const tokenRef = useRef(ctToken)
  tokenRef.current = ctToken

  // **늦게 온 옛 명부 응답이 새 값을 덮지 않게 한다** (2026-09-28). 이 화면은 시퀀스 방송마다
  // 명부를 다시 읽으므로 방송이 잇따르면 읽기가 겹친다. 옛 응답을 쓰면 폼 미리보기가 옛 행군으로
  // 다음 방송까지 남는다. 규칙과 까닭은 `src/web/access/read-order.ts` 가 진다.
  const rosterOrder = useRef(newReadOrder())

  const readRosters = useCallback(async (): Promise<void> => {
    const ticket = issue(rosterOrder.current)
    const result = await call<{ rosters: Roster[] }>(routes.rosters, { token: tokenRef.current })
    if (result.ok && accept(rosterOrder.current, ticket)) {
      setAllies(result.data.rosters.filter((row) => row.side === 'ally'))
    }
  }, [])

  /**
   * 지금 상태를 통째로 다시 읽는다. **처음 뜰 때와 소켓이 다시 붙을 때 둘 다 부른다.**
   *
   * 재접속에서 다시 읽지 않으면 끊겨 있던 동안의 방송을 통째로 놓치고, 화면은 낡은 값을
   * 그대로 든 채 아무 신호도 내지 않는다. 그럴듯한 숫자가 떠 있으므로 사람은 그것이 지금
   * 값이라고 믿는다. 노트북이 잠깐 잠들거나 배경 탭이 얼면 실제로 그 상태가 되는데, 이
   * 화면은 CT 가 전투 내내 띄워 두는 화면이다. 접속 층이 죽은 세션에서 같은 종류의
   * 실패를 한 번 만났고, 그쪽은 「입장했습니다」를 띄운 채 영원히 재접속했다.
   */
  const refresh = useCallback(async (): Promise<void> => {
    const [head] = await Promise.all([
      call<Sequence>(routes.sequence, { token: tokenRef.current }),
      readRosters(),
    ])
    if (head.ok) setSequence(head.data)
  }, [readRosters])

  useEffect(() => {
    void (async () => {
      await refresh()
      setLoaded(true)
    })()
  }, [refresh])

  useEffect(() => {
    if (ctToken === null || ctToken === '') return
    // **자기 소켓을 열지 않는다.** 서버의 연결 표는 세션 토큰 하나당 소켓 하나만 들고 새
    // 소켓이 붙으면 앞 소켓을 끊는다. 여기서 하나를 더 열면 셸의 구독과 서로를 쫓아내며
    // 2초마다 깜빡이고, 그 깜빡임은 화면에 「잠깐 멈췄다 온 값」으로만 보인다.
    return subscribeServerEvents(ctToken, {
      onEvent: (message) => {
        if (message.kind !== 'sequence.updated' || message['sequence'] === undefined) return
        setSequence(message['sequence'] as Sequence)
        // 명부의 행군이 바뀌어 온 방송일 수 있다. 좌열 폼의 네 옵션 미리보기는 고른
        // 사람의 **지금** 행군을 쓰므로 목록도 함께 다시 읽는다 — 시퀀스만 갱신하면
        // 이미 적재된 줄은 새 값으로 보이는데 폼의 미리보기만 옛 값으로 남는다.
        void readRosters()
      },
      onStatus: (status) => {
        // **다시 붙을 때마다 통째로 다시 읽는다.** 끊겨 있던 동안의 방송을 놓치면 화면은
        // 낡은 값을 든 채 아무 신호도 내지 않는다 — 그럴듯한 숫자가 떠 있으므로 사람은
        // 그것이 지금 값이라고 믿는다. 첫 연결에서 한 번 더 읽는 것이 낭비로 보이지만,
        // 그 낭비를 아끼려고 「첫 연결인가」를 들고 있으면 그 판단이 틀리는 날 조용히 낡는다.
        if (status.connected) void refresh()
      },
    })
  }, [ctToken, readRosters, refresh])

  /** 문 하나를 두드리고 응답을 시퀀스로 갈아 끼운다. 실패 문구는 서버의 것을 그대로 쓴다. */
  const send = useCallback(
    async (route: { method: string; path: string }, body: unknown): ActionResult => {
      const result = await call<Sequence>(route, { body, token: tokenRef.current })
      if (!result.ok) return { error: result.message, next: null }
      setSequence(result.data)
      return { error: null, next: result.data }
    },
    [],
  )

  const store = useMemo<SequenceStore>(
    () => ({
      sequence,
      allies,
      loaded,
      addRally: (input) => send(routes.sequenceRallyAdd, input),
      removeRally: (rallyId) => send(routes.sequenceRallyRemove, { rallyId }),
      confirmSequence: () => send(routes.sequenceConfirm, {}),
      setHandle: (handleMs) => send(routes.sequenceHandle, { handleMs }),
      dispatch: () => send(routes.sequenceDispatch, {}),
      discard: () => send(routes.sequenceDiscard, {}),
      reopen: () => send(routes.sequenceReopen, {}),
      retime: (handleMs) => send(routes.sequenceRetime, { handleMs }),
      editRally: (input) => send(routes.sequenceRallyEdit, input),
    }),
    [allies, loaded, send, sequence],
  )

  return store
}
